import * as Sentry from '@sentry/browser'

import { Session } from 'modules/core/hooks/useGetCurrentSession'
import { buildWebpageUrl } from 'modules/core/lib/buildUrl'

const SCRIPT_ID = 'hubspot-meetings-script-embed'
const SCRIPT_SRC = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js'
const MEETINGS_CONTAINER_ID = 'meetings-iframe-container'

export const MEETING_VALID_ISO_CODES = [
  'es',
  'it',
  'fr',
  'pt',
  'us',
  'mx',
  'br',
  'de',
  'ca',
  'ie',
  'gb',
]

type ContactInfo = {
  country: string
  firstName: string
  lastName: string
  email: string
  company: string
  companySize: number
  companyId: string
}

export type MeetingPayload = {
  meetingBookSucceeded: boolean
  meetingBookFailed: boolean
  meetingsPayload: {
    bookingResponse: {
      event: {
        duration: number
        dateString: string
        dateTime: number
      }
    }
    userSlug?: string
  }
}

type PartialContactInfo = Omit<ContactInfo, 'companySize' | 'country' | 'companyId'>

const initializeCalendar = async (
  sdrName: string,
  meetingsWrapperId: string,
  contactInfo: PartialContactInfo,
  isProduction: boolean,
  onMeetingBooked?: (data: MeetingPayload) => void
) => {
  const meetingsWrapper = document.querySelector(`#${meetingsWrapperId}`)

  if (!meetingsWrapper) return

  if (!document.querySelector(`#${SCRIPT_ID}`)) {
    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    meetingsWrapper.appendChild(script)
  }

  const url = new URL(`https://meetings.hubspot.com/${sdrName}?embed=true`)
  const params = new URLSearchParams({
    firstName: contactInfo.firstName,
    lastName: contactInfo.lastName,
    email: contactInfo.email,
    company: contactInfo.company,
  })
  const completeUrl = `${url}&${params}`
  const meetingsContainer = document.createElement('div')
  meetingsContainer.className = MEETINGS_CONTAINER_ID
  meetingsContainer.dataset.src = completeUrl
  meetingsWrapper.appendChild(meetingsContainer)

  window.addEventListener(
    'message',
    function receiveMessage(e) {
      try {
        if (e.data.meetingsPayload && onMeetingBooked) {
          onMeetingBooked(e.data)
        }
      } catch (err) {
        if (isProduction) Sentry.captureException(err)
      }
    },
    false
  )
}

type FindSdrParams = {
  country: string
  companySize: string
}

const findValidSdr = async (
  { companySize, country }: FindSdrParams,
  captureExplicitSentryErrors: boolean
): Promise<string | null> => {
  const fetchUrl = new URL(buildWebpageUrl('/api/hubspot/meetings'))
  fetchUrl.searchParams.append('companySize', companySize)
  fetchUrl.searchParams.append('country', country)

  const { sdrName } = await fetch(fetchUrl.href, {})
    .then((res) => res.json())
    .catch((e) => {
      if (captureExplicitSentryErrors) {
        Sentry.captureException(e)
      }
      return { sdrName: null }
    })

  return sdrName
}

export const findValidAM = async (
  companyId: string,
  captureExplicitSentryErrors: boolean
): Promise<string | null> => {
  const { accountManagerName } = await fetch(
    buildWebpageUrl(`/api/hubspot/upsellMeeting?companyId=${companyId}`)
  )
    .then((res) => res.json())
    .catch((e) => {
      if (captureExplicitSentryErrors) {
        Sentry.captureException(e)
      }
      return { accountManagerName: null }
    })

  return accountManagerName
}

export const loadMeetingsCalendar = async (
  meetingsWrapperId: string,
  captureExplicitSentryErrors: boolean,
  isUpsellMeeting: boolean,
  session: Session,
  onMeetingBooked?: (data: MeetingPayload) => void
) => {
  const {
    email,
    access: { firstName, lastName },
    company: { id: companyId, name: companyName, size: companySize, legalEntities },
  } = session

  let sdrName: string | null

  if (isUpsellMeeting) {
    sdrName = await findValidAM(String(companyId), captureExplicitSentryErrors)
  } else {
    sdrName = await findValidSdr(
      { companySize, country: legalEntities[0]?.country ?? '' },
      captureExplicitSentryErrors
    )
  }

  if (!sdrName) return

  await initializeCalendar(
    sdrName,
    meetingsWrapperId,
    {
      company: companyName,
      email: email ?? '',
      firstName,
      lastName,
    },
    captureExplicitSentryErrors,
    onMeetingBooked
  )
}
